/* eslint formatjs/no-literal-string-in-jsx: "error" */
import { Box, FormControlLabel, Radio, Stack, Typography, radioClasses, useTheme } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import React, { useCallback, useEffect } from "react"
import {
  WhatKindOfJourneyHeaderText,
  JustForNowCoreText,
  JustForNowProText,
  AccessCareText
} from "../../../carrot-plans/steps/journey/WhatKindOfJourneyStep"
import { useJourneyExperiments, useTrackExperimentEvent } from "./hooks/useExperiments"
import { RegistrationFlowLayout } from "./RegistrationLayouts"
import { SkipButton, ContinueButton, RegistrationButtonFooter } from "./RegistrationButtons"
import { getHeap } from "utils/heap"
import { FormRadioGroup, useWatch, Form } from "@carrotfertility/carotene-core-x"
import { useConvertTranslatedLabel } from "services/common-forms/useConvertTranslatedLabel"
import { FormTextFieldWithCharacterCount } from "services/common-forms/FormTextFieldWithCharacterCount"
import { RegistrationFlowTitle } from "./RegistrationFlowTitle"

interface WhatKindOfJourneyProps {
  journey: string | null
  setJourney: (journey: string) => void
  somethingElseJourney: string | null
  setSomethingElseJourney: (somethingElseJourney: string) => void
  journeyOptions: any
  isCarrotCore: boolean
  skipped: boolean
  setSkipped: (skipped: boolean) => void
  onBack: () => void
  onComplete: () => void
}

function FormRadioButtons({ journeyOptions, ...otherProps }): JSX.Element {
  const radioOptions = useConvertTranslatedLabel(journeyOptions)

  return (
    <FormRadioGroup
      name="journey"
      options={radioOptions}
      {...otherProps}
      renderItem={({ formControlLabelProps, radioProps }) => {
        return (
          <FormControlLabel
            key={radioProps.value as string}
            {...formControlLabelProps}
            sx={(theme) => ({
              padding: theme.spacing(theme.tokens.spacing.sm),
              borderRadius: theme.tokens.borderRadius.md,
              borderStyle: "solid",
              borderWidth: "2px",
              width: "fit-content",
              alignItems: "center",
              borderColor: radioProps.checked ? theme.palette.secondary.main : "transparent",
              [`:not(.${radioClasses.disabled}):hover`]: {
                bgcolor: theme.palette.secondary.light
              },
              [`&.${radioClasses.disabled}`]: {
                borderColor: radioProps.checked ? theme.palette.action.disabled : "transparent"
              }
            })}
            control={<Radio {...radioProps} />}
          />
        )
      }}
    />
  )
}

function OptionalSomethingElseTextField() {
  const selectedValue = useWatch({ name: "journey" })
  if (selectedValue !== "SOMETHING_ELSE") {
    return null
  }
  return (
    <FormTextFieldWithCharacterCount
      name="somethingElseJourney"
      helperText={<FormattedMessage defaultMessage="Optional details about your journey" />}
    />
  )
}

export function WhatKindOfJourney({
  setJourney,
  setSomethingElseJourney,
  journeyOptions,
  isCarrotCore,
  setSkipped,
  onBack,
  onComplete,
  journey,
  somethingElseJourney
}: WhatKindOfJourneyProps): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const { journeySelectionInRegistrationExperiment } = useJourneyExperiments()
  const { trackExperimentEvent } = useTrackExperimentEvent()

  const trackPageView = useCallback(
    () => getHeap().track("JourneySelectionRegFlow", { cohort: journeySelectionInRegistrationExperiment }),
    [journeySelectionInRegistrationExperiment]
  )

  const onSubmit = (formValues) => {
    if (formValues.journey?.length) {
      setJourney(formValues.journey)
      setSkipped(false)
      trackExperimentEvent("registration-journey-selected")
    } else {
      setJourney(null)
      setSkipped(true)
    }
    setSomethingElseJourney(formValues.somethingElseJourney)
    onComplete()
  }

  useEffect(() => trackPageView(), [trackPageView])

  const header = intl.formatMessage(WhatKindOfJourneyHeaderText)

  return (
    <>
      <RegistrationFlowTitle title={header} />
      <RegistrationFlowLayout
        header={intl.formatMessage(WhatKindOfJourneyHeaderText)}
        description={
          <Stack
            spacing={theme.spacing(theme.tokens.spacing.md)}
            marginTop={(theme) => theme.spacing(theme.tokens.spacing.xs)}
          >
            <p>
              {isCarrotCore ? (
                <Typography>{intl.formatMessage(JustForNowCoreText)}</Typography>
              ) : (
                <Typography>{intl.formatMessage(JustForNowProText)}</Typography>
              )}
            </p>
            <p>
              <Typography>{intl.formatMessage(AccessCareText)}</Typography>
            </p>
          </Stack>
        }
        interactiveElements={
          <Form onSubmit={(formValues) => onSubmit(formValues)} defaultValues={{ journey, somethingElseJourney }}>
            <Box marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
              <Stack spacing={theme.spacing(theme.tokens.spacing.sm)}>
                <FormRadioButtons journeyOptions={journeyOptions} />
                <OptionalSomethingElseTextField />
              </Stack>
              <RegistrationButtonFooter
                onBack={onBack}
                skipButton={
                  <SkipButton onClick={() => onSubmit({})}>
                    <FormattedMessage defaultMessage="Skip for now" />
                  </SkipButton>
                }
                continueButton={
                  <ContinueButton>
                    <FormattedMessage defaultMessage="Continue" />
                  </ContinueButton>
                }
              />
            </Box>
          </Form>
        }
      />
    </>
  )
}
